import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_BANNERS_QUERY, CREATE_BANNER_MUTATION, UPDATE_BANNER_MUTATION, DELETE_BANNER_MUTATION } from '../../apollo/queries';
import { uploadFileToS3 } from '../../utils/s3Upload';
import BannerModal from './BannerModal';
import '../css/DesignManagementPage.css';

const DesignManagementPage = () => {
    const navigate = useNavigate();
    const fileInputRefs = useRef({});
    
    // State management
    const [searchPosition, setSearchPosition] = useState('전체');
    const [selectedBanners, setSelectedBanners] = useState(new Set());
    
    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);

    // GraphQL hooks
    const { data, loading, error, refetch } = useQuery(GET_BANNERS_QUERY, {
        fetchPolicy: 'cache-and-network'
    });

    const [createBanner] = useMutation(CREATE_BANNER_MUTATION, {
        update(cache, { data: { createBanner } }) {
            if (createBanner.success && createBanner.banner) {
                const existingData = cache.readQuery({ query: GET_BANNERS_QUERY });
                cache.writeQuery({
                    query: GET_BANNERS_QUERY,
                    data: {
                        getBanners: [...existingData.getBanners, createBanner.banner]
                    }
                });
            }
        },
        onError: (error) => alert(`배너 생성 실패: ${error.message}`)
    });

    const [updateBanner] = useMutation(UPDATE_BANNER_MUTATION, {
        update(cache, { data: { updateBanner } }) {
            if (updateBanner.success && updateBanner.banner) {
                const existingData = cache.readQuery({ query: GET_BANNERS_QUERY });
                const updatedBanners = existingData.getBanners.map(banner => 
                    banner.id === updateBanner.banner.id ? updateBanner.banner : banner
                );
                cache.writeQuery({
                    query: GET_BANNERS_QUERY,
                    data: { getBanners: updatedBanners }
                });
            }
        },
        onError: (error) => alert(`배너 수정 실패: ${error.message}`)
    });

    const [deleteBanner] = useMutation(DELETE_BANNER_MUTATION, {
        update(cache, { data: { deleteBanner } }) {
            if (deleteBanner.success) {
                const existingData = cache.readQuery({ query: GET_BANNERS_QUERY });
                const remainingBanners = existingData.getBanners.filter(banner => 
                    !selectedBanners.has(banner.id)
                );
                cache.writeQuery({
                    query: GET_BANNERS_QUERY,
                    data: { getBanners: remainingBanners }
                });
                setSelectedBanners(new Set()); // 선택된 배너 초기화
            }
        },
        onError: (error) => alert(`배너 삭제 실패: ${error.message}`)
    });

    // Handle search
    const handleSearch = () => {
        refetch();
    };

    // Handle banner selection
    const handleBannerSelection = (bannerId, isSelected) => {
        const newSelected = new Set(selectedBanners);
        if (isSelected) {
            newSelected.add(bannerId);
        } else {
            newSelected.delete(bannerId);
        }
        setSelectedBanners(newSelected);
    };

    // Handle link URL update
    const handleLinkUrlUpdate = async (bannerId, linkUrl) => {
        try {
            await updateBanner({
                variables: {
                    id: bannerId,
                    input: {
                        linkUrl: linkUrl || null
                    }
                }
            });
        } catch (error) {
            console.error('링크 URL 업데이트 실패:', error);
        }
    };

    // Handle select all
    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            const allIds = new Set(banners.map(banner => banner.id));
            setSelectedBanners(allIds);
        } else {
            setSelectedBanners(new Set());
        }
    };

    // Handle delete selected banners
    const handleDeleteSelected = async () => {
        if (selectedBanners.size === 0) {
            alert('삭제할 배너를 선택해주세요.');
            return;
        }

        if (!window.confirm(`선택한 ${selectedBanners.size}개의 배너를 삭제하시겠습니까?`)) {
            return;
        }

        try {
            const deletePromises = Array.from(selectedBanners).map(bannerId => 
                deleteBanner({ variables: { id: bannerId } })
            );
            
            await Promise.all(deletePromises);
            setSelectedBanners(new Set());
            alert('선택한 배너가 삭제되었습니다.');
        } catch (error) {
            console.error('배너 삭제 에러:', error);
        }
    };

    // Handle add banner click
    const handleAddBannerClick = () => {
        setEditingBanner(null);
        setIsModalOpen(true);
    };
    
    // Handle edit banner click
    const handleEditBannerClick = (banner) => {
        setEditingBanner(banner);
        setIsModalOpen(true);
    };
    
    // Handle modal close
    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingBanner(null);
    };

    // Data processing
    const banners = data?.banners || [];
    const filteredBanners = banners.filter(banner => 
        searchPosition === '전체' || banner.position === searchPosition
    );

    if (loading) {
        return (
            <div className="admin-main-content">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>배너 정보를 불러오는 중...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-main-content">
                <div className="error-container">
                    <p>배너 정보를 불러오는 중 오류가 발생했습니다.</p>
                    <button onClick={() => refetch()}>다시 시도</button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 디자인 관리</span>
            </div>
            <div className="admin-main-content">
                <aside className="admin-sidebar">
                    <div className="sidebar-menu">
                        <h3>디자인 관리</h3>
                        <ul>
                            <li className="active">배너 관리</li>
                        </ul>
                    </div>
                </aside>
                <main className="admin-content-design">
                    <div className="page-header">
                        <div className="page-title-bar"></div>
                        <h2>디자인 관리</h2>
                    </div>

                    <div className="design-search-filters">
                        <label>코드검색</label>
                        <select 
                            className="filter-select"
                            value={searchPosition}
                            onChange={(e) => setSearchPosition(e.target.value)}
                        >
                            <option value="전체">전체</option>
                            <option value="첫번째">첫번째</option>
                            <option value="두번째">두번째</option>
                            <option value="세번째">세번째</option>
                            <option value="네번째">네번째</option>
                            <option value="하단">하단</option>
                        </select>
                    </div>
                    <button className="search-button-design" onClick={handleSearch}>검 색</button>

                    <div className="design-list-container">
                        <div className="design-list-header">
                            <span>총 배너 수 : {filteredBanners.length} 개</span>
                            <button className="btn-add-banner" onClick={handleAddBannerClick}>+ 배너 추가</button>
                        </div>
                        <table className="design-table">
                            <thead>
                                <tr>
                                    <th>
                                        <input 
                                            type="checkbox" 
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                            checked={selectedBanners.size > 0 && selectedBanners.size === filteredBanners.length}
                                        />
                                    </th>
                                    <th>노출</th>
                                    <th>노출위치</th>
                                    <th>이미지</th>
                                    <th>링크 URL</th>
                                    <th>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBanners.map(banner => {
                                    return (
                                        <tr key={banner.id}>
                                            <td data-label="선택">
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedBanners.has(banner.id)}
                                                    onChange={(e) => handleBannerSelection(banner.id, e.target.checked)}
                                                />
                                            </td>
                                            <td data-label="노출">
                                                <input 
                                                    type="checkbox" 
                                                    checked={banner.isVisible}
                                                    readOnly
                                                />
                                            </td>
                                            <td data-label="노출위치">
                                                <span>{banner.position}</span>
                                            </td>
                                            <td data-label="이미지">
                                                <div className="image-url-display">
                                                    <span>{banner.imageUrl ? '이미지 업로드됨' : '이미지 없음'}</span>
                                                    {banner.imageUrl && (
                                                        <a href={banner.imageUrl} target="_blank" rel="noopener noreferrer" 
                                                           style={{ marginLeft: '10px', fontSize: '12px' }}>
                                                            미리보기
                                                        </a>
                                                    )}
                                                </div>
                                            </td>
                                            <td data-label="링크 URL">
                                                <input 
                                                    type="url" 
                                                    defaultValue={banner.linkUrl || ''}
                                                    onBlur={(e) => handleLinkUrlUpdate(banner.id, e.target.value)}
                                                    placeholder="https://example.com"
                                                    style={{ 
                                                        width: '200px', 
                                                        padding: '5px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '3px'
                                                    }}
                                                />
                                            </td>
                                            <td data-label="관리">
                                                <button 
                                                    className="btn-edit"
                                                    onClick={() => handleEditBannerClick(banner)}
                                                >
                                                    수정
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredBanners.length === 0 && (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                                            등록된 배너가 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <div className="design-list-actions">
                            <button className="btn-delete-selected" onClick={handleDeleteSelected}>
                                선택 삭제
                            </button>
                        </div>
                    </div>
                </main>
            </div>
            
            {/* Banner Modal */}
            <BannerModal 
                isOpen={isModalOpen}
                onClose={handleModalClose}
                banner={editingBanner}
            />
        </>
    );
};

export default DesignManagementPage; 